import { type FieldValues, useFormState } from 'react-hook-form';
import { Button, Stack } from 'components/ui';
import { AuthTextField } from 'pages/Auth/components/AuthTextField';
import { getVisibleFieldErrorMessage, hasVisibleFieldError } from 'pages/Auth/utils';
import type { AuthIdentifierRequestFormProps } from './AuthIdentifierRequestForm.props';

const AuthIdentifierRequestForm = <TFieldValues extends FieldValues>({
  autoComplete = 'email',
  disabled = false,
  extraAction,
  fieldName,
  form,
  hiddenErrorTypes,
  id,
  label,
  onSubmit,
  placeholder,
  submitDisabled = false,
  submitLabel,
  submitLoading = false,
  submitTestId,
  submitVisualVariant,
}: AuthIdentifierRequestFormProps<TFieldValues>) => {
  const formState = useFormState({
    control: form.control,
    name: fieldName,
  });
  const fieldError = form.getFieldState(fieldName, formState).error;
  const helperText = getVisibleFieldErrorMessage({
    error: fieldError,
    hiddenErrorTypes,
  });
  const hasBlockingFieldError = hasVisibleFieldError({
    error: fieldError,
    hiddenErrorTypes,
  });

  return (
    <Stack component="form" noValidate onSubmit={onSubmit} spacing={2.25}>
      <AuthTextField
        autoComplete={autoComplete}
        disabled={disabled}
        error={Boolean(fieldError)}
        helperText={helperText}
        id={id}
        label={label}
        placeholder={placeholder}
        {...form.register(fieldName)}
      />
      <Button
        data-testid={submitTestId}
        disabled={disabled || submitDisabled || hasBlockingFieldError}
        loading={submitLoading}
        loadingBehavior="inline"
        type="submit"
        variant="contained"
        visualVariant={submitVisualVariant}
      >
        {submitLabel}
      </Button>
      {extraAction}
    </Stack>
  );
};

export default AuthIdentifierRequestForm;
